import React from 'react';

import ContextSingleton from '../__singletons__/context-singleton';
import { ALERT_SEVERITY_ENUM } from '../enums';
import * as helpers from '../helpers';
import * as types from '../types';

type IUseUpdateUiMetadata<TResource = any> = {
  resourceId: number;
  resourceType: string;
  resource?: { shared?: boolean };
  setAlert?: (_alert: types.common.Alert | null) => void;
  onSuccess?: (_resource: TResource) => void;
};

/**
 * Hook that provides a function to update UI metadata for a resource
 *
 * @template TResource Type of the resource being updated
 * @template TUiMetaData Type of the UI metadata being updated
 * @param resourceId - ID of the resource to update
 * @param resourceType - Type of the resource (e.g., 'universes', 'signals')
 * @param resource - Optional resource object to check if shared
 * @param setAlert - Optional function to set alert messages for errors
 * @param onSuccess - Optional callback executed on successful update
 * @returns Function to update UI metadata
 *
 */
const useUpdateUiMetadata = <TResource = any, TUiMetaData = any>({
  resourceId,
  resourceType,
  resource,
  setAlert,
  onSuccess,
}: IUseUpdateUiMetadata<TResource>) => {
  const updateUiMetadata = React.useCallback(
    async (uiMetaData: TUiMetaData): Promise<void> => {
      if (resource?.shared) return;
      try {
        const updateMetadata = {
          ui_metadata: uiMetaData,
          skip_signature_check: true,
        };

        const api = ContextSingleton.getInstance().api;
        const resourceEndpoint = (api as any)[resourceType];
        if (!resourceEndpoint || !resourceEndpoint.update) {
          throw new Error(`API endpoint not found for resource type: ${resourceType}`);
        }

        const respMetadata = await resourceEndpoint.update(resourceId, updateMetadata);
        if (onSuccess) onSuccess(respMetadata.data);
      } catch (error) {
        const errorMessage = `Error updating the resource metadata - ${helpers.parseApiError(
          error as types.common.ApiError
        )}`;

        if (setAlert) setAlert({ severity: ALERT_SEVERITY_ENUM.ERROR, message: errorMessage });
        throw new Error(errorMessage);
      }
    },
    [resourceId, resourceType, resource, setAlert, onSuccess]
  );

  return updateUiMetadata;
};

export default useUpdateUiMetadata;
